export function isValidTimezone(timezone: string | undefined | null): timezone is string {
  if (!timezone) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

export function defaultScheduleTimezone(instanceTimezone?: string | null): string {
  if (isValidTimezone(instanceTimezone)) return instanceTimezone
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return isValidTimezone(browserTimezone) ? browserTimezone : 'UTC'
}

function wallClockAsUTC(instantMs: number, formatter: Intl.DateTimeFormat): number {
  const parts = formatter.formatToParts(new Date(instantMs))
  const get = (type: string) => {
    const value = parts.find(part => part.type === type)?.value
    if (value === undefined) throw new RangeError('Unable to determine schedule timezone offset')
    return Number(value)
  }
  const date = new Date(0)
  date.setUTCFullYear(get('year'), get('month') - 1, get('day'))
  date.setUTCHours(get('hour'), get('minute'), get('second'), 0)
  return date.getTime()
}

/** Returns the first valid instant on a calendar date in the schedule's timezone. */
export function zonedMidnightToISO(dateString: string, timezone: string): string {
  const fields = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString)
  const year = Number(fields?.[1])
  const month = Number(fields?.[2])
  const day = Number(fields?.[3])
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  if (!fields || year < 1 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new RangeError('Invalid schedule start date')
  }
  const utcMidnight = date.getTime()
  if (!isValidTimezone(timezone)) return date.toISOString()

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const dayMs = 86_400_000
  const offsets = new Set<number>()
  for (const dayOffset of [-2, -1, 0, 1, 2]) {
    const instant = utcMidnight + dayOffset * dayMs
    offsets.add(wallClockAsUTC(instant, formatter) - instant)
  }
  const candidates = Array.from(offsets, offset => utcMidnight - offset).sort((a, b) => a - b)
  const midnight = candidates.find(candidate => wallClockAsUTC(candidate, formatter) === utcMidnight)
  if (midnight !== undefined) return new Date(midnight).toISOString()

  let before = Math.min(...candidates)
  let after = Math.max(...candidates)
  if (wallClockAsUTC(before, formatter) >= utcMidnight || wallClockAsUTC(after, formatter) < utcMidnight) {
    throw new RangeError('Schedule start date does not exist in this timezone')
  }
  while (after - before > 1000) {
    const middle = before + Math.floor((after - before) / 2000) * 1000
    if (wallClockAsUTC(middle, formatter) < utcMidnight) before = middle
    else after = middle
  }
  const firstWallTime = wallClockAsUTC(after, formatter)
  if (firstWallTime < utcMidnight || firstWallTime >= utcMidnight + dayMs) {
    throw new RangeError('Schedule start date does not exist in this timezone')
  }
  return new Date(after).toISOString()
}

export function todayInTimezone(timezone: string, now: Date = new Date()): string {
  const zone = isValidTimezone(timezone) ? timezone : 'UTC'
  return new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(now)
}
